/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * DatePicker body hook - only for day period type
 * - render calendar day cells
 *
 * @author Yakov Gushchin
 * @version 1.10.0
 * @since 1.10.0
 */

import { Moment } from 'moment';

import { IDatePickerBodyCellProps, IDatePickerBodyProps } from '../../types/datePickerTypes';

type TUseDatePickerBody = IDatePickerBodyProps;

const useDatePickerBodyByDay = (props: TUseDatePickerBody) => {

    const {
        maxDate,
        minDate,
        period,
        selectedDay,
    } = props;

    const renderCells = () => {

        /* Define time period */
        const startOfMonth = period.clone().startOf('month');
        const startOfWeek = startOfMonth.clone().startOf('isoWeek');
        const endOfMonth = period.clone().endOf('month');
        const endOfWeek = endOfMonth.clone().endOf('isoWeek');

        /* Add all days in one month*/
        const data: Moment[] = [];
        let s = startOfWeek.clone();

        while (s <= endOfWeek) {
            data.push(s);
            s = s.clone().add(1, 'day');
        }

        /* Complement array to 42 elements (7 horizontal x 6 vertical cells) */
        if (data.length < 42) {
            for (let fill = data.length; fill < 42; fill++) {
                data.push(s);
                s = s.clone().add(1, 'day');
            }
        }
        const resolvedData: IDatePickerBodyCellProps[] = [];

        // Fill each row (week) with day info
        for (let cxi = 0; cxi < 6; cxi++) {
            const rxi = cxi * 7;
            // set props to each day in week row
            for (let cxj = 0; cxj < 7; cxj++) {
                const day = data[rxi + cxj];
                const isDayNotBetween = isNotBetween(minDate, maxDate, day);
                const isInvisible = period.month() > day.month() || period.month() < day.month();
                const isSelected = selectedDay && day.format('YYYY-MM-DD') === selectedDay.format('YYYY-MM-DD');

                const className: string = [
                    `${isSelected ? ' selected' : ''}`,
                    `${isDayNotBetween ? ' disabled' : ' available'}`,
                    `${isInvisible ? ' invisible' : ''}`,
                ].join('')

                resolvedData.push({ day, className, key: rxi + cxj  });
            }
        }

        return resolvedData;
    }

    return {
        renderCells,
    };
};

const isNotBetween = (minDate: any, maxDate: any, day: any) =>
    (!!minDate && day.isBefore(minDate, 'day')) || (!!maxDate && day.isAfter(maxDate, 'day'));

export default useDatePickerBodyByDay;
